import { isSupabaseConfigured, supabase } from './supabase';

export type ToolService = 'simulator' | 'plc' | 'faults' | 'lighting';

export type ToolAccessResult = {
  allowed: boolean;
  mode: 'subscriber' | 'free' | 'blocked' | 'open';
  remaining: number | null;
  limit: number | null;
  username?: string | null;
  expiresAt?: string | null;
  systemReady: boolean;
  error?: string;
};

const DEVICE_KEY = 'murta-tool-device-v1';
const SESSION_KEY = 'murta-tool-session-v1';

function safeStorage(kind: 'local' | 'session') {
  if (typeof window === 'undefined') return null;
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function randomId(prefix: string) {
  const id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}${id}`;
}

export function getToolDeviceId() {
  const storage = safeStorage('local');
  if (!storage) return randomId('d_');
  const current = storage.getItem(DEVICE_KEY);
  if (current) return current;
  const created = randomId('d_');
  storage.setItem(DEVICE_KEY, created);
  return created;
}

export function getToolSessionToken() {
  return safeStorage('local')?.getItem(SESSION_KEY) ?? null;
}

export function clearToolSessionToken() {
  safeStorage('local')?.removeItem(SESSION_KEY);
}

function saveToolSessionToken(token: string) {
  safeStorage('local')?.setItem(SESSION_KEY, token);
}

function normalizeRpcData(value: unknown): Record<string, unknown> | null {
  if (!value) return null;
  if (Array.isArray(value)) {
    const first = value[0];
    return first && typeof first === 'object' ? (first as Record<string, unknown>) : null;
  }
  return typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

export async function validateToolSession(): Promise<ToolAccessResult | null> {
  const client = supabase;
  const token = getToolSessionToken();
  if (!client || !isSupabaseConfigured || !token) return null;

  const { data, error } = await client.rpc('tool_validate_session', { p_token: token });
  if (error) {
    if (/function .* does not exist|could not find the function/i.test(error.message)) return null;
    return {
      allowed: false,
      mode: 'blocked',
      remaining: 0,
      limit: null,
      systemReady: true,
      error: error.message,
    };
  }

  const row = normalizeRpcData(data);
  if (!row || row.allowed !== true) {
    clearToolSessionToken();
    return null;
  }

  return {
    allowed: true,
    mode: 'subscriber',
    remaining: null,
    limit: null,
    username: typeof row.username === 'string' ? row.username : null,
    expiresAt: typeof row.expires_at === 'string' ? row.expires_at : null,
    systemReady: true,
  };
}

export async function checkToolAccess(service: ToolService): Promise<ToolAccessResult> {
  if (!supabase || !isSupabaseConfigured) {
    return { allowed: true, mode: 'open', remaining: null, limit: null, systemReady: false };
  }

  const subscribed = await validateToolSession();
  if (subscribed?.allowed) return subscribed;

  const attemptId = randomId('a_').replace(/^a_/, '');
  const { data, error } = await supabase.rpc('tool_consume_free_access', {
    p_service: service,
    p_device_id: getToolDeviceId(),
    p_attempt_id: attemptId,
  });

  if (error) {
    // قبل تنفيذ ملف SQL الجديد نترك الأدوات تعمل حتى لا ينغلق الموقع على الزوار.
    return {
      allowed: true,
      mode: 'open',
      remaining: null,
      limit: null,
      systemReady: false,
      error: error.message,
    };
  }

  const row = normalizeRpcData(data);
  if (!row) {
    return { allowed: true, mode: 'open', remaining: null, limit: null, systemReady: false };
  }

  const allowed = row.allowed === true;
  const limit = Number.isFinite(Number(row.limit)) ? Number(row.limit) : null;
  const remaining = Number.isFinite(Number(row.remaining)) ? Number(row.remaining) : null;
  const mode = row.mode === 'open' ? 'open' : allowed ? 'free' : 'blocked';

  return {
    allowed,
    mode,
    remaining,
    limit,
    systemReady: true,
  };
}

export async function loginToolUser(username: string, password: string) {
  const client = supabase;
  if (!client || !isSupabaseConfigured) return { ok: false, message: 'نظام الدخول غير مربوط.' };

  const { data, error } = await client.rpc('tool_access_login', {
    p_username: username.trim(),
    p_password: password,
  });

  if (error) return { ok: false, message: 'تعذر تسجيل الدخول. حاول مرة ثانية.' };
  const row = normalizeRpcData(data);
  if (!row || row.allowed !== true || typeof row.token !== 'string') {
    return { ok: false, message: typeof row?.message === 'string' ? row.message : 'اسم المستخدم أو كلمة المرور غير صحيحة.' };
  }

  saveToolSessionToken(row.token);
  return {
    ok: true,
    username: typeof row.username === 'string' ? row.username : username.trim(),
    expiresAt: typeof row.expires_at === 'string' ? row.expires_at : null,
  };
}
